import { useRouter } from 'next/router';

import { ApiErrors, isApiError } from '@common/api-request';
import { isKakaotalk } from '@common/device-manager';
import { useKakaoAPI } from '@common/hooks';
import { airbridgeTracker } from '@common/marketing-tracker';
import { Toast } from '@common/overlay-manager';
import { loginKakao } from '@common/utils';

import { useLikeProductConfig } from '../context/ConfigContext';
import { useMutateWebLikeProduct } from '../query/useMutateWebLikeProduct';

interface UseLikeConfig {
  onLikeChange: (checked: boolean, productId: string) => void;
  webToastStyle?: React.CSSProperties;
}

type AirbridgeConfig = {
  productID: unknown;
  productName: unknown;
  categoryID: string;
  categoryName: string;
  brandID: unknown;
  brandName: unknown;
  price: unknown;
  currency?: unknown;
};

/**
 * ZigZag Web에서 like 동작 시 전달하는 값
 * @docs https://www.notion.so/croquis/widget-like-product-bb6ac56bf316499ea5f85e30faadad03
 */
export type WebLikeProductPayload = {
  checked: boolean;
  productData: {
    catalog_product_id: string;
    shop_id: string;
  };
  airbridge: AirbridgeConfig;
  is_posty?: boolean;
  sendLog?: (checked: boolean) => void;
};

export const useWebLikeProduct = ({ onLikeChange, webToastStyle }: UseLikeConfig) => {
  const { saveMutate, removeMutate } = useMutateWebLikeProduct();
  const router = useRouter();
  const { loginUrl } = useLikeProductConfig();
  useKakaoAPI(() => isKakaotalk());

  const goToLogin = () => {
    const redirectLoginUrl = loginUrl();

    if (!redirectLoginUrl) {
      return;
    }

    if (isKakaotalk()) {
      loginKakao(redirectLoginUrl);
      return;
    }
    // NOTE: next 기반이 아닌 경우 (e.g. 직진배송관) router가 없기 때문에 옵셔널 처리.
    if (router) {
      router.push(redirectLoginUrl);
      return;
    }

    window.location.assign(redirectLoginUrl);
  };

  const sendAirbridgeEvents = (log: AirbridgeConfig) => {
    if (log) {
      airbridgeTracker.events.send('airbridge.addToWishlist', {
        semanticAttributes: {
          products: [{ ...log, currency: 'KRW' }],
        },
        label: log.categoryName.split(',')[0],
      });
    }
  };

  const onWebLikeProduct = async ({ checked, productData, airbridge, sendLog }: WebLikeProductPayload) => {
    onLikeChange(checked, productData.catalog_product_id);
    sendLog?.(checked);

    try {
      if (checked) {
        await saveMutate(productData);
        Toast.show('찜한 상품에 추가했어요', undefined, webToastStyle);
        sendAirbridgeEvents(airbridge);
      } else {
        await removeMutate(productData);
        Toast.show('찜한 상품에서 삭제했어요', undefined, webToastStyle);
      }
    } catch (error) {
      if (isApiError(error) && error.message === ApiErrors.NOT_LOGGED_IN) {
        return goToLogin();
      }
      console.error(error);
      Toast.show('찜하기에 실패했습니다. 잠시 후에 다시 시도해주세요.');
    }
  };

  return {
    onWebLikeProduct,
  };
};
